import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import {
  ArrowUpRight,
  Target,
  SlidersHorizontal,
  CheckCircle2,
  Network,
} from "lucide-react";
import { RefreshingBadge } from "@/components/common";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth";
import AssignedMetricsDrawer from "./AssignedMetricsDrawer";
import DataEntryCompletionDrawer from "./DataEntryCompletionDrawer";
import SelectedOrganizationsDrawer from "./SelectedOrganizationsDrawer";
import TargetSetupDrawer from "./TargetSetupDrawer";
import SummaryCard from "./SummaryCard";
import SummaryCardSkeleton from "./skeleton/SummaryCardSkeleton";
import {
  useDashboardKpiStore,
  useDashboardFiltersStore,
} from "../hooks/useDashboardStore";
import { filterBucketsByCategory } from "../domains/category";
import { formatPercentage } from "../domains/formatting";
import { buildSelectedOrganizationEntries } from "../utils/hierarchy-picker";
import { buildScopedOrganizationPickerTree } from "../utils/dashboard-filter-options";

const HIERARCHY_LABELS = {
  all: "All Organizations",
  company: "All Companies",
  businessUnit: "All Business Units",
  site: "All Sites",
  country: "All Countries",
};

const EMPTY_ORG_IDS = [];
const DETAILS_ACTION_CLASSNAME =
  "inline-flex shrink-0 items-center gap-1 text-[11px] font-medium text-primary transition-colors group-hover:text-primary/80";

const buildOrganizationHierarchyLabel = (entry) => {
  if (!entry) return "";
  if (entry.fullHierarchyLabel) return entry.fullHierarchyLabel;

  return [entry.parentHierarchyLabel, entry.name].filter(Boolean).join(" > ");
};

const KpiCards = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const { summary, loading, hasLoadedOnce } = useDashboardKpiStore();
  const { filters, available } = useDashboardFiltersStore();
  const [isAssignedMetricsOpen, setIsAssignedMetricsOpen] = useState(false);
  const [isTargetSetupOpen, setIsTargetSetupOpen] = useState(false);
  const [isDataEntryCompletionOpen, setIsDataEntryCompletionOpen] =
    useState(false);
  const [isSelectedOrganizationsOpen, setIsSelectedOrganizationsOpen] =
    useState(false);

  const selectedOrgIds = filters.orgIds || EMPTY_ORG_IDS;

  const isAllOrganizationsSelected = selectedOrgIds.length === 0;
  const isCountry = filters.hierarchy == "country";

  const scopedOrganizationTree = useMemo(
    () =>
      buildScopedOrganizationPickerTree({
        orgTree: available.orgTree || [],
        reportingYears: available.reportingYears || [],
        selectedYears: filters.years,
        companyId: user?.companyId,
        hierarchy: filters.hierarchy,
      }),
    [
      available.orgTree,
      available.reportingYears,
      filters.hierarchy,
      filters.years,
      user?.companyId,
    ],
  );

  const selectedOrganizationEntries = useMemo(
    () =>
      buildSelectedOrganizationEntries(
        scopedOrganizationTree,
        filters.hierarchy,
        selectedOrgIds,
      ),
    [filters.hierarchy, scopedOrganizationTree, selectedOrgIds],
  );

  const selectedOrganizationsCount = selectedOrganizationEntries.length;

  const selectedOrganizationsSubtitle = isAllOrganizationsSelected
    ? t(HIERARCHY_LABELS[filters.hierarchy] || HIERARCHY_LABELS.all)
    : selectedOrganizationEntries.length === 1
      ? buildOrganizationHierarchyLabel(selectedOrganizationEntries[0]) ||
        selectedOrganizationEntries[0].name
      : t("Filtered organization scope");

  if (!hasLoadedOnce && loading) {
    return (
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SummaryCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  const buckets = filterBucketsByCategory(
    summary.categories,
    new Set(filters.categories),
  );

  const totalUniqueAssigned = buckets.reduce(
    (s, b) => s + (b.uniqueAssigned || 0),
    0,
  );
  const totalUniqueWithTarget = buckets.reduce(
    (s, b) => s + (b.uniqueWithTarget || 0),
    0,
  );
  const totalUniqueWithData = buckets.reduce(
    (s, b) => s + (b.uniqueWithData || 0),
    0,
  );
  const targetPct = totalUniqueAssigned
    ? (totalUniqueWithTarget / totalUniqueAssigned) * 100
    : 0;
  const dataPct = totalUniqueAssigned
    ? (totalUniqueWithData / totalUniqueAssigned) * 100
    : 0;
  const detailsAction = (
    <span className={DETAILS_ACTION_CLASSNAME}>
      {t("Details")}
      <ArrowUpRight className="h-3.5 w-3.5" />
    </span>
  );

  return (
    <div className="relative">
      {hasLoadedOnce && loading && <RefreshingBadge />}
      <div
        className={cn(
          "grid grid-cols-1 gap-2 transition-opacity md:grid-cols-2 xl:grid-cols-4",
          hasLoadedOnce && loading && "opacity-60",
        )}
      >
        <button
          type="button"
          onClick={() => setIsAssignedMetricsOpen(true)}
          className="group h-full w-full text-left"
        >
          <SummaryCard
            title={t("Assigned Metrics")}
            value={totalUniqueAssigned}
            subtitle={t("Total unique metrics assigned")}
            icon={Target}
            tone="primary"
            action={detailsAction}
          />
        </button>
        <button
          type="button"
          onClick={() => setIsTargetSetupOpen(true)}
          className="group h-full w-full text-left"
        >
          <SummaryCard
            title={t("Target Setup Completion")}
            value={formatPercentage(targetPct)}
            subtitle={t(
              `${totalUniqueWithTarget} of ${totalUniqueAssigned} metrics have targets defined`,
            )}
            icon={SlidersHorizontal}
            tone="muted"
            action={detailsAction}
          />
        </button>
        <button
          type="button"
          onClick={() => setIsDataEntryCompletionOpen(true)}
          className="group h-full w-full text-left"
        >
          <SummaryCard
            title={t("Data Entry Completion")}
            value={formatPercentage(dataPct)}
            subtitle={t(
              `${totalUniqueWithData} of ${totalUniqueAssigned} have data entries`,
            )}
            icon={CheckCircle2}
            tone="success"
            action={detailsAction}
          />
        </button>
        <button
          type="button"
          onClick={() => setIsSelectedOrganizationsOpen(true)}
          className="group h-full w-full text-left"
        >
          <SummaryCard
            title={
              isCountry ? t("Selected Countries") : t("Selected Organizations")
            }
            value={selectedOrganizationsCount}
            subtitle={selectedOrganizationsSubtitle}
            icon={Network}
            tone="primary"
            action={detailsAction}
          />
        </button>
      </div>
      <AssignedMetricsDrawer
        open={isAssignedMetricsOpen}
        onOpenChange={setIsAssignedMetricsOpen}
      />
      <TargetSetupDrawer
        open={isTargetSetupOpen}
        onOpenChange={setIsTargetSetupOpen}
      />
      <DataEntryCompletionDrawer
        open={isDataEntryCompletionOpen}
        onOpenChange={setIsDataEntryCompletionOpen}
      />
      <SelectedOrganizationsDrawer
        open={isSelectedOrganizationsOpen}
        onOpenChange={setIsSelectedOrganizationsOpen}
      />
    </div>
  );
});

export default KpiCards;
